/**
 * @fileoverview Componente QrMessage para envío de mensajes y recordatorios optimizado (Mercately).
 * Implementa separación estricta de responsabilidades con hooks especializados por capas,
 * optimización de performance y arquitectura Atomic Design nivel molecular.
 * @module QrMessage
 * @category Component - Molecule
 * @since 3.0.0
 * @version 3.1.0
 */

import React, { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTraceUpdate } from '../../hooks/shared/useDebugRenders';

import FormProviderMC from '../../components/FormProviderMC';
import InputMC from '../../components/InputMC';
import DropzoneFileUploader from '../../components/DropzoneFileUploader';
import DatePickerMC from '../../components/DatePickerMC';
import DropdownMC from '../../components/DropdownMC';
import QrPreview from './components/QrPreview';
import CustomerInfo from './components/CustomerInfo';
import { DEFAULT_FILE_SIZE_TRANSFER } from '../../../constants/chatFileSizes';
import useNewChatMsIntegration from '../../hooks/api-template-message/data/useNewChatMsIntegration';
import { integrationLabel } from 'services/inbox/resolveNewChatMsIntegration';

// Hooks especializados por capas
import useQrMessageForm from '../../hooks/qr-message/forms/useQrMessageForm';
import useQrMessageActions from '../../hooks/qr-message/actions/useQrMessageActions';
import { sharingInformationService } from '../../../services/sharingInformationService';

const QrMessage = memo(({
  hasCustomer,
  closeModal,
  type,
  currentCustomer,
  onOutboundSendingChange,
  msContext
}) => {
  const { t } = useTranslation('NewChat');
  const sendInFlightRef = useRef(false);
  const fallbackMs = useNewChatMsIntegration(currentCustomer, { enabled: !msContext });

  const {
    isMsWhatsapp,
    showSelector,
    integrations,
    selectedIntegration,
    selectIntegration,
    chatId,
    loading: integrationsLoading
  } = msContext || fallbackMs;

  // Configuraciones memoizadas para hooks
  const formConfig = useMemo(() => ({
    type,
    defaultValues: {
      templateText: '',
      sendDate: new Date(),
      attachedFile: []
    }
  }), [type]);

  const actionsConfig = useMemo(() => ({ currentCustomer }), [currentCustomer]);

  // Hooks especializados por capas
  const {
    methods,
    handleSubmit,
    isValid
  } = useQrMessageForm(formConfig);

  const {
    processQrMessage
  } = useQrMessageActions(actionsConfig);

  const handleIntegrationSelect = useCallback((integrationId) => {
    selectIntegration(integrationId);
  }, [selectIntegration]);

  const onSubmit = useCallback(async (data) => {
    if (sendInFlightRef.current) return;

    if (isMsWhatsapp && !selectedIntegration) {
      return;
    }

    sendInFlightRef.current = true;
    onOutboundSendingChange?.(true);

    const operationCallbacks = {
      onSuccess: () => {
        sharingInformationService.setSubject({
          refreshListDataReminder: true,
        });
        sharingInformationService.setSubject({});
        closeModal();
      },
      onError: (error) => {
        // console.error('Error en QrMessage onSubmit:', error);
      }
    };

    try {
      const sendMsContext = isMsWhatsapp
        ? {
          isMsWhatsapp: true,
          integration: selectedIntegration,
          chatId
        }
        : { isMsWhatsapp: false };

      await processQrMessage(type, data, operationCallbacks, sendMsContext);
    } finally {
      sendInFlightRef.current = false;
      onOutboundSendingChange?.(false);
    }
  }, [type, processQrMessage, closeModal, onOutboundSendingChange, isMsWhatsapp, selectedIntegration, chatId]);

  useTraceUpdate('QrMessage', {
    hasCustomer,
    type,
    currentCustomerId: currentCustomer?.id,
    isValid
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <div className="tw-wrapper">
      <FormProviderMC
        id="qr-message-form"
        methods={methods}
        onSubmit={handleSubmit(onSubmit)}
        className="tw-flex tw-flex-col tw-h-full"
      >
        <CustomerInfo currentCustomer={actionsConfig?.currentCustomer} />

        <div className="tw-flex-1 tw-space-y-6">
          {showSelector && (
            <DropdownMC
              key={selectedIntegration?.id || 'empty-integration'}
              name="platformRetailerId"
              id="platformRetailerId"
              label={t('apiMessage.selectIntegration')}
              placeholder={t('apiMessage.selectIntegrationPlaceholder')}
              options={integrations.map((integration) => ({
                id: integration.id,
                label: integrationLabel(integration)
              }))}
              defaultValue={selectedIntegration?.id || ''}
              onSelect={handleIntegrationSelect}
              useFloatingLabel
              search
              disabled={integrationsLoading || integrations.length === 0}
            />
          )}

          <div>
            <p className="tw-font-medium tw-my-4">
              {t('qrMessage.writeMessage')}
            </p>

            <InputMC
              id="templateText"
              name="templateText"
              label={t('qrMessage.messageLabel')}
              placeholder={t('qrMessage.messagePlaceholder')}
              textarea
              rows={4}
              useFloatingLabel
            />
          </div>

          {type === 'reminder' && (
            <DatePickerMC
              name="sendDate"
              label={t('apiMessage.sendDateTime')}
              dateFormat="Pp"
              timeInputLabel="Hora"
              showTimeInput
              minDate={new Date()}
              className="tw-w-full"
            />
          )}

          <DropzoneFileUploader
            name="attachedFile"
            maxFiles={1}
            maxSize={DEFAULT_FILE_SIZE_TRANSFER}
            accept="image/jpg, image/jpeg, image/png, application/pdf, video/mp4, video/3gpp"
            placeholder="Arrastra tu imagen, PDF o video, o haz clic para subir"
          />

          <QrPreview />
        </div>
      </FormProviderMC>
    </div>
  );
});

QrMessage.displayName = 'QrMessage';

export default QrMessage;
